import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom'
import { AddJVPointModal, computeTotalPoints, ConfirmationModal, LoadingSpinner, useAuth, ViewJVPointModal } from '../components'
import { Button, Card } from '../components/ui'
import { JVPoint, JVPointVolunteer, JVPointsMetaProps } from "../data/types";
import { ChevronLeftIcon, ChevronRightIcon, DeleteIcon, EditIcon, EyeIcon, PlusIcon, SearchIcon, StarIcon } from "../icons";
import { API_BASE_URL, ConfirmationModalMeta, LoadingSpinnerMeta } from "../data";

type Props = {
    meta: JVPointsMetaProps
}


type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type VolunteerFilters = {
    name: string
    jamat_khana: string
}

const emptyFilters: VolunteerFilters = { name: '', jamat_khana: '' }

type SortOption = 'total_points_desc' | 'total_points_asc' | 'name_asc' | 'name_desc'

const SORT_OPTIONS: { value: SortOption; label: string; sortBy: 'total_points' | 'name'; sortDir: 'asc' | 'desc' }[] = [
    { value: 'total_points_desc', label: 'Total Points (High to Low)', sortBy: 'total_points', sortDir: 'desc' },
    { value: 'total_points_asc', label: 'Total Points (Low to High)', sortBy: 'total_points', sortDir: 'asc' },
    { value: 'name_asc', label: 'Name (A to Z)', sortBy: 'name', sortDir: 'asc' },
    { value: 'name_desc', label: 'Name (Z to A)', sortBy: 'name', sortDir: 'desc' },
]

const DEFAULT_SORT: SortOption = 'total_points_desc'

type VolunteerPointDetails = {
    records: JVPoint[]
    pagination: PaginationData
    loading: boolean
    error: string | null
}

const ITEMS_PER_PAGE = 10;
const DETAILS_PER_PAGE = 5;

const defaultPagination: PaginationData = {
    current_page: 1,
    last_page: 1,
    per_page: ITEMS_PER_PAGE,
    total: 0
}

const defaultDetailsPagination: PaginationData = {
    current_page: 1,
    last_page: 1,
    per_page: DETAILS_PER_PAGE,
    total: 0
}

function formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}


export const JVPoints = (props: Props) => {
    const { meta } = props;
    const { user } = useAuth();
    const isJvLead = user?.role === 'jv_lead';
    const [jvPointsRecords, setJvPointRecords] = useState<JVPointVolunteer[]>([]);
    const [pagination, setPagination] = useState<PaginationData>(defaultPagination);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [expandedCsids, setExpandedCsids] = useState<Set<number>>(new Set());
    const [detailsByCsid, setDetailsByCsid] = useState<Record<number, VolunteerPointDetails>>({});
    const [addModalVolunteerId, setAddModalVolunteerId] = useState<number | null>(null);
    const [editingEntry, setEditingEntry] = useState<{ volunteerId: number; entry: JVPoint } | null>(null);
    const [viewingEntry, setViewingEntry] = useState<JVPoint | null>(null);
    const [deletingEntry, setDeletingEntry] = useState<{ volunteerId: number; entry: JVPoint } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [nameFilter, setNameFilter] = useState('');
    const [jamatKhanaFilter, setJamatKhanaFilter] = useState('');
    const [appliedFilters, setAppliedFilters] = useState<VolunteerFilters>(emptyFilters);
    const [sortOption, setSortOption] = useState<SortOption>(DEFAULT_SORT);

    const fetchJVRecords = async (page: number = 1, filters: VolunteerFilters = appliedFilters, sort: SortOption = sortOption) => {
        const sortConfig = SORT_OPTIONS.find(o => o.value === sort) ?? SORT_OPTIONS[0];
        const params = new URLSearchParams({
            per_page: String(ITEMS_PER_PAGE),
            page: String(page),
            sort_by: sortConfig.sortBy,
            sort_dir: sortConfig.sortDir,
        });
        if (filters.name) params.append('name', filters.name);
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana);

        const response = await fetch(`${API_BASE_URL}/jv-point?${params.toString()}`, {
            method: "GET",
            credentials: 'include'
        });
        if (!response.ok) {
            setJvPointRecords([]);
            setPagination(defaultPagination);
            throw new Error('Failed to retrieved Junior Volunteer Data')
        }
        const body = await response.json();
        setPagination({
            current_page: body.current_page,
            last_page: body.last_page,
            per_page: body.per_page,
            total: body.total
        });
        setJvPointRecords(body.data);
    }

    const loadJVRecords = async (page: number = 1, filters: VolunteerFilters = appliedFilters, sort: SortOption = sortOption) => {
        setLoading(true);
        setError(null);
        try {
            await fetchJVRecords(page, filters, sort);
        }
        catch (err) {
            const message = err instanceof (Error) ? err.message : 'Failed to retrieved Junior Volunteer Data';
            setError(message);
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadJVRecords();
    }, [])

    const handleVolunteerPageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            loadJVRecords(page, appliedFilters, sortOption);
        }
    }

    const handleFilterSearch = () => {
        const filters: VolunteerFilters = { name: nameFilter.trim(), jamat_khana: jamatKhanaFilter.trim() };
        setAppliedFilters(filters);
        loadJVRecords(1, filters, sortOption);
    }

    const handleFilterClear = () => {
        setNameFilter('');
        setJamatKhanaFilter('');
        setAppliedFilters(emptyFilters);
        loadJVRecords(1, emptyFilters, sortOption);
    }

    const handleFilterKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleFilterSearch();
    }

    const handleSortChange = (value: SortOption) => {
        setSortOption(value);
        loadJVRecords(1, appliedFilters, value);
    }

    const fetchVolunteerPointDetails = async (volunteerId: number, page: number = 1) => {
        setDetailsByCsid(prev => ({
            ...prev,
            [volunteerId]: {
                records: prev[volunteerId]?.records ?? [],
                pagination: prev[volunteerId]?.pagination ?? defaultDetailsPagination,
                loading: true,
                error: null
            }
        }));

        try {
            const response = await fetch(
                `${API_BASE_URL}/jv-point/volunteer/${volunteerId}?per_page=${DETAILS_PER_PAGE}&page=${page}`,
                { method: "GET", credentials: 'include' }
            );
            if (!response.ok) {
                throw new Error('Failed to retrieve point details');
            }
            const body = await response.json();
            setDetailsByCsid(prev => ({
                ...prev,
                [volunteerId]: {
                    records: body.data,
                    pagination: {
                        current_page: body.current_page,
                        last_page: body.last_page,
                        per_page: body.per_page,
                        total: body.total
                    },
                    loading: false,
                    error: null
                }
            }));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to retrieve point details';
            setDetailsByCsid(prev => ({
                ...prev,
                [volunteerId]: {
                    records: prev[volunteerId]?.records ?? [],
                    pagination: prev[volunteerId]?.pagination ?? defaultDetailsPagination,
                    loading: false,
                    error: message
                }
            }));
        }
    }

    const handleToggleDetails = (volunteerId: number) => {
        const isExpanded = expandedCsids.has(volunteerId);

        setExpandedCsids(prev => {
            const next = new Set(prev);
            if (isExpanded) {
                next.delete(volunteerId);
            } else {
                next.add(volunteerId);
            }
            return next;
        });

        if (!isExpanded && !detailsByCsid[volunteerId]) {
            fetchVolunteerPointDetails(volunteerId);
        }
    }

    const handleDetailPageChange = (volunteerId: number, page: number) => {
        const detail = detailsByCsid[volunteerId];
        if (!detail) return;
        if (page >= 1 && page <= detail.pagination.last_page) {
            fetchVolunteerPointDetails(volunteerId, page);
        }
    }

    const handlePointSaved = (volunteerId: number) => {
        // Refresh this volunteer's entry list and the summary list's total_points together,
        // since total_points is computed server-side and isn't safe to derive locally here.
        // Stay on whichever page of each list the admin is currently viewing.
        fetchVolunteerPointDetails(volunteerId, detailsByCsid[volunteerId]?.pagination.current_page ?? 1);
        fetchJVRecords(pagination.current_page, appliedFilters, sortOption).catch(() => {
            // Summary list refresh failing shouldn't disrupt the rest of the page;
            // the per-volunteer detail list above still reflects the new/updated entry.
        });
    }

    const handleDeleteConfirm = async () => {
        if (!deletingEntry) return;

        setIsDeleting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/jv-point/${deletingEntry.entry.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to delete point entry.');
            }
            handlePointSaved(deletingEntry.volunteerId);
            setDeletingEntry(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete point entry.';
            const volunteerId = deletingEntry.volunteerId;
            setDetailsByCsid(prev => ({
                ...prev,
                [volunteerId]: {
                    records: prev[volunteerId]?.records ?? [],
                    pagination: prev[volunteerId]?.pagination ?? defaultDetailsPagination,
                    loading: false,
                    error: message
                }
            }));
            setDeletingEntry(null);
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div>
                <Link to="/admin" className="inline-flex items-center mb-8 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    Back to Admin Hub
                </Link>
            </div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{meta.title}</h1>
                <p className="mt-1 text-gray-600 dark:text-gray-400">{meta.description}</p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        placeholder="Search by name..."
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleFilterKeyDown}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    {!isJvLead && (
                        <input
                            type="text"
                            placeholder="Search by jamat khana..."
                            value={jamatKhanaFilter}
                            onChange={(e) => setJamatKhanaFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                    )}
                    <div className="flex gap-2">
                        <Button onClick={handleFilterSearch} size="sm">
                            <SearchIcon className="w-4 h-4 me-2" />
                            Search
                        </Button>
                        <Button onClick={handleFilterClear} variant="outline" size="sm">
                            Clear
                        </Button>
                    </div>
                </div>

                <select
                    value={sortOption}
                    onChange={(e) => handleSortChange(e.target.value as SortOption)}
                    className="ms-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                    {SORT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>

            {loading && (
                <div className="flex justify-center items-center min-h-[300px]">
                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                </div>
            )}

            {error && (
                <p className="text-red-600 dark:text-red-400">{error}</p>
            )}

            {!loading && !error && jvPointsRecords.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400">No junior volunteers found.</p>
            )}

            {!loading && !error && jvPointsRecords.length > 0 && (
                <div className="flex flex-col gap-3">
                    {jvPointsRecords.map((record) => {
                        const isExpanded = expandedCsids.has(record.csid);
                        const details = detailsByCsid[record.csid];

                        return (
                            <Card key={record.csid}>
                                <div className="p-4 flex items-center justify-between">
                                    <div>
                                        <span className="font-medium text-gray-900 dark:text-gray-100">
                                            {record.first_name} {record.last_name}
                                        </span>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Age: <strong>{record.age ?? 'Not Provided'}</strong> &middot; Country: <strong>{record.country || 'Not Provided'}</strong> &middot; Jamat Khana: <strong>{record.jamat_khana || 'Not Provided'} </strong>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                            <StarIcon className="w-4 h-4" />
                                            {record.total_points} pts
                                        </span>
                                        <Button
                                            variant="outline"
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleToggleDetails(record.csid)}
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                            {isExpanded ? 'Hide Details' : 'Show Details'}
                                        </Button>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                                        {!isJvLead && (
                                            <div className="mb-4">
                                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Contact Details</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Email: {record.email || 'Not Provided'}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Phone: {record.phone || 'Not Provided'}</p>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Point History</h3>
                                            <Button
                                                variant="outline"
                                                color="primary"
                                                size="sm"
                                                onClick={() => setAddModalVolunteerId(record.csid)}
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Add Point Record
                                            </Button>
                                        </div>

                                        {details?.loading && (
                                            <div className="flex justify-center items-center min-h-[150px]">
                                                <LoadingSpinner meta={LoadingSpinnerMeta} />
                                            </div>
                                        )}

                                        {details && !details.loading && details.error && (
                                            <p className="text-red-600 dark:text-red-400">{details.error}</p>
                                        )}

                                        {details && !details.loading && !details.error && details.records.length === 0 && (
                                            <p className="text-gray-500 dark:text-gray-400">No point entries found.</p>
                                        )}

                                        {details && !details.loading && !details.error && details.records.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                {details.records.map((entry) => (
                                                    <Card key={entry.id}>
                                                        <div className="flex items-center justify-between py-2 px-3">
                                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                {formatDate(entry.date_of_entry)}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                                                                    <StarIcon className="w-4 h-4" />
                                                                    {computeTotalPoints(entry)} pts
                                                                </span>
                                                                <Button
                                                                    variant="icon"
                                                                    color="primary"
                                                                    size="sm"
                                                                    onClick={() => setViewingEntry(entry)}
                                                                >
                                                                    <EyeIcon className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="icon"
                                                                    color="primary"
                                                                    size="sm"
                                                                    onClick={() => setEditingEntry({ volunteerId: record.csid, entry })}
                                                                >
                                                                    <EditIcon className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="icon"
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => setDeletingEntry({ volunteerId: record.csid, entry })}
                                                                >
                                                                    <DeleteIcon className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}

                                        {details && !details.loading && !details.error && details.pagination.last_page > 1 && (
                                            <div className="flex justify-center items-center gap-4 mt-4">
                                                <Button
                                                    onClick={() => handleDetailPageChange(record.csid, details.pagination.current_page - 1)}
                                                    disabled={details.pagination.current_page === 1}
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    <ChevronLeftIcon className="w-5 h-5" />
                                                </Button>

                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    Page {details.pagination.current_page} of {details.pagination.last_page}
                                                </span>

                                                <Button
                                                    onClick={() => handleDetailPageChange(record.csid, details.pagination.current_page + 1)}
                                                    disabled={details.pagination.current_page === details.pagination.last_page}
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    <ChevronRightIcon className="w-5 h-5" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {!loading && !error && pagination.last_page > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                    <Button
                        onClick={() => handleVolunteerPageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        variant="outline"
                        size="sm"
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </Button>

                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {pagination.current_page} of {pagination.last_page}
                    </span>

                    <Button
                        onClick={() => handleVolunteerPageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        variant="outline"
                        size="sm"
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </Button>
                </div>
            )}

            {addModalVolunteerId !== null && (
                <AddJVPointModal
                    isOpen={addModalVolunteerId !== null}
                    onClose={() => setAddModalVolunteerId(null)}
                    volunteerId={addModalVolunteerId}
                    onSaved={() => handlePointSaved(addModalVolunteerId)}
                />
            )}

            {editingEntry !== null && (
                <AddJVPointModal
                    isOpen={editingEntry !== null}
                    onClose={() => setEditingEntry(null)}
                    volunteerId={editingEntry.volunteerId}
                    initialEntry={editingEntry.entry}
                    onSaved={() => handlePointSaved(editingEntry.volunteerId)}
                />
            )}

            {viewingEntry !== null && (
                <ViewJVPointModal
                    isOpen={viewingEntry !== null}
                    onClose={() => setViewingEntry(null)}
                    entry={viewingEntry}
                />
            )}

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={deletingEntry !== null}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeletingEntry(null)}
                title="Delete Point Record"
                message={`Are you sure you want to delete the point entry dated ${formatDate(deletingEntry?.entry.date_of_entry) ?? ''}? This action cannot be undone.`}
                isLoading={isDeleting}
            />
        </div>
    );
}
